/**
 * Runtime context builder + generic payload merge for subscription-based APMs.
 *
 * Backend delivers PM configuration (capability flags) but cannot know the live
 * hosted-page state: which invoices are selected, which route is active, what the
 * cart/estimate holds, or which saved PM was chosen. This module reads that state
 * from Vuex and route, builds a surface-aware snapshot, and merges the generic
 * subscription details (schedule, line items) into the chargebee-js payload.
 *
 * Stripe Klarna-specific extras (subscriptionEstimateJson, mandate handle, portal
 * numeric id, retainPaymentMethod) are layered on top by
 * `stripe-klarna-subscription-apm-policy.js` after this module runs.
 * Nothing here should reference Stripe or Klarna directly.
 */

import {
  deriveSubscriptionContext,
  shouldSendLineItems,
  shouldSendSubscriptionDetails,
  isSubscriptionBasedApm
} from '@/utils/subscription-based-apm-helper';

function readQueryFromLocation() {
  if (typeof window === 'undefined' || !window.location || !window.location.search) {
    return {};
  }
  const params = new URLSearchParams(window.location.search);
  const query = {};
  params.forEach((value, key) => {
    query[key] = value;
  });
  return query;
}

function normalizeLineItemsFromGetters(getters) {
  const est = getters.estimate || {};
  return getters.line_items || est.line_items || est.lineItems || [];
}

/**
 * For paynow, flattens {@code line_items} from each selected invoice so line-item payload can
 * be attached.
 */
function lineItemsFromPaynowSelectedInvoices(getters) {
  const invs = getters.selected_invoices || [];
  const acc = [];
  invs.forEach((inv) => {
    if (inv && Array.isArray(inv.line_items) && inv.line_items.length) {
      acc.push(...inv.line_items);
    }
  });
  return acc;
}

/**
 * @param {object} getters store.getters or rootGetters
 * @param {object} routeQuery normalized query object
 */
function buildSubscriptionRuntimeSnapshotCore(getters, routeQuery) {
  const hp = getters.hosted_page_type;
  const query = routeQuery || {};

  const base = {
    hosted_page_type: hp,
    ...(Object.keys(query).length ? { route: { query } } : {}),
    subscriptions: getters.subscriptions || [],
    // Used only to recognize the referenced subscription, never for single-vs-multi counting.
    child_subscriptions: getters.child_subscriptions || []
  };

  if (hp === 'checkout') {
    const snapshot = {
      ...base,
      subscription_handle: getters.subscription_handle,
      estimate: getters.estimate,
      order_estimate_holder: getters.order_estimate_holder,
      line_items: normalizeLineItemsFromGetters(getters),
      cart_plan: getters.cart_plan
    };
    return snapshot;
  }

  if (hp === 'portal') {
    const snapshot = {
      ...base,
      subscription_handle: getters.subscription_handle,
      subHandle: query.sub_handle || getters.subscription_handle,
      estimate: getters.estimate,
      order_estimate_holder: getters.order_estimate_holder,
      line_items: normalizeLineItemsFromGetters(getters)
    };
    return snapshot;
  }

  if (hp === 'paynow') {
    const snapshot = {
      ...base,
      selected_invoices: getters.selected_invoices || [],
      line_items: lineItemsFromPaynowSelectedInvoices(getters)
    };
    return snapshot;
  }

  const snapshot = {
    ...base,
    subscription_handle: getters.subscription_handle,
    estimate: getters.estimate,
    order_estimate_holder: getters.order_estimate_holder,
    line_items: normalizeLineItemsFromGetters(getters),
    selected_invoices: getters.selected_invoices
  };
  return snapshot;
}

/**
 * Builds a surface-aware snapshot from Vuex getters + Vue Router route.
 * Each surface (checkout / portal / paynow) exposes a different subset of getters;
 * this function normalizes them so policy functions receive a uniform object.
 *
 * @param {import('vuex').Store} store
 * @param {object} [route] optional Vue Router route (uses window location query if omitted)
 */
export function buildSubscriptionRuntimeSnapshot(store, route) {
  const routeQuery =
    route && route.query ? route.query : readQueryFromLocation();
  return buildSubscriptionRuntimeSnapshotCore(store.getters, routeQuery);
}

/** For getters (e.g. pm_list_filtered) where only rootGetters is available */
export function buildSubscriptionRuntimeSnapshotFromRootGetters(rootGetters) {
  return buildSubscriptionRuntimeSnapshotCore(rootGetters, readQueryFromLocation());
}

export function deriveSubscriptionContextFromStore(store, route) {
  const snap = buildSubscriptionRuntimeSnapshot(store, route);
  return deriveSubscriptionContext(snap);
}

export function deriveSubscriptionContextFromRootGetters(rootGetters) {
  return deriveSubscriptionContext(
    buildSubscriptionRuntimeSnapshotFromRootGetters(rootGetters)
  );
}

/**
 * Resolve gateway PM config for a saved payment source (customer PM row).
 */
export function resolveGatewayPmForPaymentSource(paymentSource, pmList) {
  if (!paymentSource || !pmList || !pmList.length) {
    return null;
  }
  const byType = pmList.filter((p) => p.type === paymentSource.type);
  if (byType.length === 0) {
    return null;
  }
  const gw = paymentSource.gateway_name || paymentSource.gateway;
  if (gw != null && String(gw) !== '') {
    const strict = byType.filter(
      (p) => {
        const pGw = p.gateway_name || p.gateway;
        return pGw && String(pGw).toUpperCase() === String(gw).toUpperCase();
      }
    );
    if (strict.length === 1) {
      return strict[0];
    }
    return null;
  }
  if (byType.length === 1) {
    return byType[0];
  }
  return null;
}

function extractCheckoutSubscriptionScheduleFromEstimateLines(getters) {
  const est = getters.estimate || {};
  const fromRoot =
    getters.line_items || est.line_items || est.lineItems || [];
  const holder = getters.order_estimate_holder || {};
  const hEst = holder.estimate || {};
  const hImm = holder.immediateEstimate || {};
  const fromHolder = hEst.line_items || hImm.line_items || hImm.lineItems || [];
  const lines = [
    ...(Array.isArray(fromRoot) ? fromRoot : []),
    ...(Array.isArray(fromHolder) ? fromHolder : [])
  ];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line) {
      continue;
    }
    const et = String(line.entity_type || line.item_type || '').toLowerCase();
    if (
      et !== 'plan' &&
      et !== 'plan_item_price' &&
      et !== 'plan_setup' &&
      et !== 'charge_item_price'
    ) {
      continue;
    }
    const d = line.details || line;
    if (d != null && d.period_unit != null && d.period != null) {
      return { frequencyUnit: d.period_unit, frequencyPeriod: d.period };
    }
  }
  return null;
}

/**
 * Parses the internal `subscription_<n>` db_id string into the numeric id expected
 * by the payment-intent confirm payload (`PaymentAttemptInput.Subscription#id`).
 * The portal and paynow resolvers in `stripe-klarna-subscription-apm-policy.js` call
 * this because they need the numeric id, not the public handle.
 */
export function parseSubscriptionDbId(dbId) {
  if (dbId == null || dbId === '') {
    return null;
  }
  const s = String(dbId);
  const m = s.match(/^subscription_(\d+)$/);
  if (!m) {
    return null;
  }
  return Number(m[1]);
}

export function extractSubscriptionScheduleForAdditionalData(store) {
  const g = store.getters;
  const hp = g.hosted_page_type;

  if (hp === 'checkout') {
    const cp = g.cart_plan;
    if (cp && cp.details && cp.details.period_unit) {
      return {
        frequencyUnit: cp.details.period_unit,
        frequencyPeriod: cp.details.period
      };
    }
    return extractCheckoutSubscriptionScheduleFromEstimateLines(g);
  }

  if (hp === 'portal') {
    const cp = g.change_order_plan || g.cart_plan;
    if (cp && cp.details && cp.details.period_unit) {
      return {
        frequencyUnit: cp.details.period_unit,
        frequencyPeriod: cp.details.period
      };
    }
    return null;
  }

  if (hp === 'paynow') {
    return extractPaynowSubscriptionSchedule(store);
  }

  return null;
}

/**
 * Paynow: recurring schedule from a single invoice’s line items (plan / plan price rows),
 * when the subscription object is not in {@code getters.subscriptions} (e.g. new subscription invoice).
 */
function extractPaynowSubscriptionScheduleFromInvoiceLines(invoices) {
  const invs = Array.isArray(invoices) ? invoices : [];
  for (let j = 0; j < invs.length; j++) {
    const inv = invs[j];
    const lines =
      (inv && inv.line_items) ||
      (inv && inv.lineItems) ||
      [];
    if (!Array.isArray(lines) || !lines.length) {
      continue;
    }
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (!line) {
        continue;
      }
      const et = String(line.entity_type || line.item_type || '').toLowerCase();
      if (
        et !== 'plan' &&
        et !== 'plan_item_price' &&
        et !== 'plan_setup' &&
        et !== 'charge_item_price'
      ) {
        continue;
      }
      const d = line.details || line;
      if (d != null && d.period_unit != null && d.period != null) {
        return { frequencyUnit: d.period_unit, frequencyPeriod: d.period };
      }
    }
  }
  return null;
}

/**
 * Paynow: schedule from customer subscription list when selected invoices imply a single subscription;
 * falls back to plan line items on the invoice when the sub is missing or has no billing period fields.
 */
function extractPaynowSubscriptionSchedule(store) {
  const g = store.getters;
  const invoices = g.selected_invoices || [];
  const ids = new Set();
  invoices.forEach((inv) => {
    const sid = inv && (inv.subscription_id ?? inv.subscriptionId);
    if (sid != null && sid !== '') {
      ids.add(String(sid));
    }
  });
  if (ids.size !== 1) {
    return null;
  }
  const subId = ids.values().next().value;
  const subs = g.subscriptions || [];
  const sub = subs.find((s) => s && String(s.id) === subId);
  if (sub) {
    const pu =
      sub.billing_period_unit ??
      sub.period_unit ??
      (sub.plan && sub.plan.period_unit) ??
      (sub.plan_details && sub.plan_details.period_unit);
    const pp =
      sub.billing_period ??
      sub.period ??
      (sub.plan && sub.plan.period) ??
      (sub.plan_details && sub.plan_details.period);
    if (pu && pp != null) {
      return { frequencyUnit: pu, frequencyPeriod: pp };
    }
  }
  return extractPaynowSubscriptionScheduleFromInvoiceLines(invoices);
}

/**
 * Merges generic subscription-based APM additionalData (subscription schedule + line items)
 * into the paymentInfo object that will be passed to chargebee-js.
 *
 * This is the generic layer. It attaches only what every subscription APM needs:
 * - `subscription` (frequencyUnit / frequencyPeriod) when policy allows sending sub details
 * - `lineItems` when the PM has `set_line_items: true` and context allows it
 *
 * Preserves all unrelated keys on paymentInfo / additionalData; does not clear
 * caller-supplied fields. Stripe Klarna-specific fields are added afterwards by
 * `mergeStripeKlarnaAdditionalData` in `stripe-klarna-subscription-apm-policy.js`.
 *
 * @param {object} opts
 * @param {object} opts.paymentInfo paymentInfo object passed to chargebee-js (may be undefined)
 * @param {import('vuex').Store} opts.store
 * @param {object} [opts.route]
 * @param {object} opts.pm gateway PM row from pm_list (capabilities)
 */
export function mergeSubscriptionApmAdditionalData({ paymentInfo, store, route, pm }) {
  const pmCfg = pm || {};
  if (!isSubscriptionBasedApm(pmCfg)) {
    const baseInfo = paymentInfo && typeof paymentInfo === 'object' ? paymentInfo : {};
    const out = Object.assign({}, baseInfo);
    if (baseInfo.additionalData) {
      out.additionalData = Object.assign({}, baseInfo.additionalData);
    }
    return out;
  }

  const derived = deriveSubscriptionContextFromStore(store, route);
  const baseInfo = paymentInfo && typeof paymentInfo === 'object' ? paymentInfo : {};
  const additionalData = Object.assign({}, baseInfo.additionalData || {});

  const sendSub = shouldSendSubscriptionDetails({ pm: pmCfg, context: derived });
  if (sendSub) {
    const schedule = extractSubscriptionScheduleForAdditionalData(store);
    if (schedule) {
      additionalData.subscription = schedule;
    }
  }

  const sendLines =
    shouldSendLineItems({ pm: pmCfg, context: derived }) &&
    derived.lineItems.length;
  if (sendLines) {
    additionalData.lineItems = derived.lineItems.slice();
  }

  const out = Object.assign({}, baseInfo);
  if (Object.keys(additionalData).length > 0) {
    out.additionalData = additionalData;
  }
  return out;
}
